import React from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';

import { extractProperty, isSet } from '@utils';
import {
  DomainsDescription,
  DomainsDescriptionId,
} from '@models/feature-descriptor-domain';
import {
  useDomainsDescriptionsTreeConfig,
  useDomainsDescriptionsTreeItemSelection,
  useDomainsDescriptionsSelectionContext,
} from '../context';
import { DescriptionsTree } from '../models';

enum SelectionIndicator {
  Checked = 'checked',
  Unchecked = 'unchecked',
  Indeterminate = 'indeterminate',
}

export const TreeItemLabel: React.FC<{
  label: string;
  nodeId: DescriptionsTree.NodeId;
  managedDescriptions: DomainsDescription[];
}> = props => {
  const { isWithCheckboxes, areCheckboxesDisabled } =
    useDomainsDescriptionsTreeConfig();
  const [selectedTreeItems, { toggleTreeItemSelection }] =
    useDomainsDescriptionsTreeItemSelection();
  const [selectedDomainDescriptionsIds, { toggleSelection }] =
    useDomainsDescriptionsSelectionContext();

  const managedDescriptionIds = React.useMemo(
    () =>
      DomainsDescriptionId.listToMap(
        props.managedDescriptions.map(extractProperty('id'))
      ),
    []
  );

  const checked = React.useMemo(
    () =>
      getSelectionState(managedDescriptionIds, selectedDomainDescriptionsIds),
    [/*props.managedDescriptions,*/ selectedDomainDescriptionsIds]
  );

  function isSelected() {
    return isSet(selectedTreeItems[props.nodeId]);
  }

  return (
    <FormControlLabel
      className={`text-ellipsis selection-indicator ${
        isSelected() ? 'selected' : ''
      }`}
      sx={{
        display: 'block',
        paddingLeft: isWithCheckboxes ? 'initial' : '10px',
      }}
      control={
        isWithCheckboxes ? (
          <Checkbox
            className={areCheckboxesDisabled ? 'disabled' : ''}
            sx={{ paddingBlock: '2px' }}
            checked={checked === SelectionIndicator.Checked}
            indeterminate={checked === SelectionIndicator.Indeterminate}
            disableRipple={areCheckboxesDisabled}
            onClick={event => {
              event.stopPropagation();
              if (areCheckboxesDisabled) {
                return;
              }
              const checked = (
                event as unknown as React.ChangeEvent<HTMLInputElement>
              ).target.checked;

              toggleSelection(
                checked,
                props.managedDescriptions.map(extractProperty('id'))
              );
            }}
          />
        ) : (
          <></>
        )
      }
      label={props.label}
      onClick={event => {
        toggleTreeItemSelection(
          props.nodeId,
          props.managedDescriptions.flatMap(extractProperty('domains'))
        );
        event.stopPropagation();
        event.preventDefault();
      }}
    />
  );
};

function getSelectionState(
  managedDescriptionIds: Map<DomainsDescriptionId.String, DomainsDescriptionId>,
  selectedIds: Map<DomainsDescriptionId.String, DomainsDescriptionId>
): SelectionIndicator {
  const managedNotSelectedIds = Array.from(managedDescriptionIds.keys()).filter(
    id => !selectedIds.has(id)
  ).length;
  const areAllChecked = managedNotSelectedIds === 0;
  const areAllUnselected = managedNotSelectedIds === managedDescriptionIds.size;
  return areAllChecked
    ? SelectionIndicator.Checked
    : areAllUnselected
    ? SelectionIndicator.Unchecked
    : SelectionIndicator.Indeterminate;
}
